import { useEffect } from "react";
import Stack from "@mui/material/Stack";
import LoadingButton from "@mui/lab/LoadingButton";
import { Form, Formik } from "formik";
import {
  Alert,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getNotificationSettings } from "app/redux/slice/supplierNotificationSlice";
import Loader from "@jumbo/components/Loader";

// The automatic rules. Each row is one event the server can fire on its own;
// the rows themselves come from the API, which seeds them from its own defaults,
// so this screen never hardcodes the list.
const NotificationSettings = ({ onClose, handleSubmit, canUpdate }) => {
  const dispatch = useDispatch();
  const { settings, settingsLoading } = useSelector(
    (state) => state?.supplierNotification
  );
  const mobileView = useMediaQuery("(max-width:575px)");

  useEffect(() => {
    dispatch(getNotificationSettings());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (settingsLoading) return <Loader />;

  return (
    <Formik
      enableReinitialize
      initialValues={{ settings: settings ?? [] }}
      onSubmit={(data, { setSubmitting }) => {
        setSubmitting(true);
        handleSubmit(data, () => setSubmitting(false));
      }}
    >
      {({ values, isSubmitting, handleChange, setFieldValue }) => (
        <Form>
          <Alert severity="info" sx={{ mb: 2 }}>
            Turn a rule off and the vendor is never notified for that event. The
            tags listed under each rule are filled in when it is sent, so every
            vendor sees their own values.
          </Alert>

          {values.settings?.map((setting, index) => (
            <Card key={setting.eventKey} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                >
                  <div>
                    <Typography variant="h5" mb={0}>
                      {setting.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {setting.description}
                    </Typography>
                  </div>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={setting.isEnabled !== false}
                        disabled={!canUpdate}
                        onChange={(e) =>
                          setFieldValue(
                            `settings.${index}.isEnabled`,
                            e.target.checked
                          )
                        }
                        color="success"
                      />
                    }
                    label={setting.isEnabled !== false ? "On" : "Off"}
                  />
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Title"
                      name={`settings.${index}.title`}
                      value={setting.title ?? ""}
                      onChange={handleChange}
                      disabled={!canUpdate || setting.isEnabled === false}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                      label="Message"
                      name={`settings.${index}.messageTemplate`}
                      value={setting.messageTemplate ?? ""}
                      onChange={handleChange}
                      disabled={!canUpdate || setting.isEnabled === false}
                    />
                  </Grid>
                  {/* Only meaningful for the "rate updated" rule - a rate
                            being set for the first time is always a change. */}
                  {setting.eventKey === "party-rate-updated" && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={setting.notifyOnlyOnPriceChange !== false}
                            disabled={!canUpdate || setting.isEnabled === false}
                            onChange={(e) =>
                              setFieldValue(
                                `settings.${index}.notifyOnlyOnPriceChange`,
                                e.target.checked
                              )
                            }
                            size="small"
                          />
                        }
                        label="Only notify when the price actually changes"
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    {/* Only the tags this particular rule can fill in. They
                        come from the API, because which tags an event
                        understands is part of the event - {{price}} means
                        nothing on an invoice notification. */}
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                      alignItems="center"
                    >
                      <Typography variant="caption" color="text.secondary">
                        Tags you can use:
                      </Typography>
                      {(setting.placeholders ?? []).map((tag) => (
                        <Chip
                          key={tag}
                          size="small"
                          variant="outlined"
                          label={tag}
                        />
                      ))}
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}

          <Stack
            spacing={{ xs: 1, sm: 2 }}
            direction="row"
            justifyContent="flex-end"
            sx={{ mt: 2 }}
          >
            <LoadingButton
              variant="outlined"
              color="secondary"
              onClick={onClose}
              size="large"
              fullWidth={mobileView}
            >
              Close
            </LoadingButton>
            {canUpdate && (
              <LoadingButton
                type="submit"
                variant="contained"
                size="large"
                loading={isSubmitting}
                loadingIndicator={<CircularProgress size={20} />}
                fullWidth={mobileView}
              >
                Save Settings
              </LoadingButton>
            )}
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default NotificationSettings;
